import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { AssetGroup } from '@app/core/model/entities/asset-group/asset-group';
import { AccessManager } from '@app/core/services/managers/access.manager';
import { ActivationEndService } from '@app/features/main/activation-end.service';
import { AssetGroupsService } from '@app/features/main/views/asset-groups/asset-groups.service';
import { AssetGroupModalService } from '@app/features/main/views/asset-groups/modals/asset-group-modal.service';
import { DocumentModalService } from '@app/features/main/views/organization-documents/modals/document-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { AppManager } from '@services/managers/app.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'asset-group-header',
  templateUrl: './asset-group-header.component.html',
  providers: [AssetGroupModalService, DocumentModalService],
  standalone: false
})
export class AssetGroupHeaderComponent implements OnInit {

  protected assetGroup: AssetGroup;
  protected readonly Permission = PermissionEnum;

  private readonly destroyRef = inject(DestroyRef);
  private readonly appManager = inject(AppManager);
  private readonly titleService = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly activationEndService = inject(ActivationEndService);
  private readonly assetGroupsService = inject(AssetGroupsService);
  private readonly assetGroupModalService = inject(AssetGroupModalService);
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly snackbarManager = inject(SnackbarManager);
  protected readonly accessManager = inject(AccessManager);

  /**
   * Get the current Asset Group entity and set a page title.
   */
  public ngOnInit(): void {
    this.assetGroup = this.appManager.currentEntity as AssetGroup;

    // Update Asset Group as it is updated outside the header
    this.assetGroupsService.assetGroupsUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(groups => {
        const updated = groups.find(g => g.id === this.assetGroup.id);
        if (updated) {
          this.assetGroup = updated;
        }
      });

    this.activationEndService.pushOptionalParams({
      'TITLE.ASSET_GROUP_IDENTIFIER': {
        identifier: this.assetGroup.identifier.toString()
      }
    });
    this.titleService.setTitle(`${this.translate.instant(
      'TITLE.ASSET_GROUP_IDENTIFIER',
      {identifier: this.assetGroup.identifier}
    )} - myA`);
  }

  /**
   * Opens a confirmation dialog to delete the current Asset Group.
   * If confirmed, deletes the Asset Group and redirects to the Asset Groups grid.
   */
  public deleteAssetGroup(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SHEET,
      NavigateToEnum.DELETE_DIALOG,
      EntityTypeEnum.ASSET_GROUP,
      this.assetGroup.id
    );

    this.assetGroupModalService.openDeleteAssetGroupsDialog([this.assetGroup]).pipe(
      filter(result => result),
      takeUntilDestroyed(this.destroyRef)
    )
      .subscribe(() => {
        this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.ASSET_GROUP_DELETED'));
        this.router.navigate(
          ['organization', this.appManager.currentOrganization.id, 'asset_groups'],
          {replaceUrl: true}
        )
          .catch(console.error);
      });
  }
}
